/**
 * The purchase orders placed under a contract: a table on the contract, and a chooser to add one.
 *
 * ED, 2026-10-28: "The table should not say References and titled Related Objects. It should say
 * Purchase Orders. There is no need for a Relationship picker." And: "give it a backing line the
 * other sections have with a border and rounded edges."
 *
 * WHAT IT IS. The source product's milestone references table, for one kind of reference: the
 * orders under an agreement are edges of the 'Contract Purchase Order' relationship type (parent
 * the contract's logical object, child the order's), seeded per realm. This pane lists those edges
 * -- ID, Name, Status, Remove -- and "Add purchase order" opens the LongSelect idiom over purchase
 * orders only: a search box, a server-paged grid, a radio, and Select. The relationship type is
 * fixed, so nothing asks for it.
 *
 * THE SAME READS AND WRITES THE RELATED OBJECTS GRID MAKES: object_relationships by parent and
 * type, object_revisions by classification for the chooser, and the relationship document to
 * create and delete an edge. Authorisation is the server's (the parent record's edit privilege);
 * a refusal is shown as the server phrased it.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import {v4 as uuidv4} from "uuid";

import ObjectList, {PickedRecord} from "../../pages/ObjectList";
import {OBJECT_CLASSIFICATIONS} from "../../util/objectClassifications";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";

const EDGE_TYPE = "Contract Purchase Order";
const RELATIONSHIP_IDOC = "v1.ObjectRelationship-001";

type Edge = {USID: string; ChildUSID?: string; ChildObjectID?: string | null; ChildName?: string | null;
             ChildStatus?: string | null};
type Order = {ObjectUSID: string; ObjectID?: string | null; Name?: string | null; Status?: string | null};

function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

/** A query-by-example document: the shape the IDOC reader dereferences (Name, Operator, Type). */
function qbe(idoc: string, component: string, members: {Name: string; Value: any; Operator: string}[],
             limit: number, offset = 0) {
    return btoa(JSON.stringify({
        PlatformMessage: {
            "-idoc": idoc, "-id": uuidv4(),
            [component]: {
                _attributes: {limit, sort: null, offset},
                QueryGroups: {QueryGroup: [{
                    Name: "primary", Operator: "AND", Type: "QueryGroup",
                    Membership: {Member: members.map(m => ({Type: "Attribute", ...m}))},
                }]},
            },
        },
    }));
}

export default function ContractPurchaseOrdersPane({objectUSID, title = "Purchase Orders", status, onChanged}: {
    /** The contract's LOGICAL object. Edges hang off the object, never a revision. */
    objectUSID: string;
    title?: string;
    /** The revision's status. Orders are added only to a DRAFT (Ed, 2026-10-28: "The Add Purchase
     *  Order button should only be enabled when the object is in DRAFT status"); a host that does
     *  not know the status leaves the button enabled. */
    status?: string | null;
    /** Told after an order is added or removed, so a host showing a count (the contracts grid's
     *  POs column) can read it again. */
    onChanged?: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [typeUSID, setTypeUSID] = useState<string | null>(null);
    const draft = status == null || String(status).toUpperCase() === "DRAFT";
    const [edges, setEdges] = useState<Edge[] | null>(null);
    const [problem, setProblem] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);

    // The relationship type, once. Seeded per realm; absent means the seed has not reached this
    // account, and the pane says so rather than offering an add that would fail.
    useEffect(() => {
        let cancelled = false;
        api.get("t1/v1/object_relationship_types", {
            params: {QBE: qbe("v1.ObjectRelationshipType-001", "ObjectRelationshipType",
                               [{Name: "Name", Value: EDGE_TYPE, Operator: "EQUAL"}], 1)},
        }).then((response: any) => {
            if (cancelled) return;
            const rows = listOf<any>(response?.data?.PlatformMessage?.ListOfObjectRelationshipType?.ObjectRelationshipType);
            setTypeUSID(rows[0]?.USID ?? null);
        }).catch(() => { if (!cancelled) setTypeUSID(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(() => {
        if (!objectUSID || !typeUSID) return;
        api.get("t1/v1/object_relationships", {
            params: {ParentUSID: objectUSID, ObjectRelationshipTypeUSID: typeUSID, Limit: 500},
            headers: {"idoc-response": RELATIONSHIP_IDOC},
        }).then((response: any) => {
            setEdges(listOf<Edge>(response?.data?.PlatformMessage?.ListOfObjectRelationship?.ObjectRelationship));
            setProblem(null);
        }).catch((failure: any) => {
            setEdges([]);
            setProblem(failure?.response?.data?.Cause ?? "The purchase orders could not be read.");
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID, typeUSID]);

    useEffect(() => { load(); }, [load]);

    const remove = (edge: Edge) => {
        setRemoving(edge.USID);
        api.delete("t1/v1/object_relationships", {
            headers: {"idoc-request": RELATIONSHIP_IDOC},
            data: {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: {USID: edge.USID}}},
        }).then(() => { load(); onChanged?.(); })
          .catch((failure: any) => setProblem(failure?.response?.data?.Cause ?? "The purchase order could not be removed."))
          .finally(() => setRemoving(null));
    };

    const add = (order: Order) => {
        if (!typeUSID) return Promise.reject(new Error("no type"));
        return api.post("t1/v1/object_relationships",
            {PlatformMessage: {"-idoc": RELATIONSHIP_IDOC, ObjectRelationship: {
                ParentUSID: objectUSID, ChildUSID: order.ObjectUSID, ObjectRelationshipTypeUSID: typeUSID}}},
            {headers: {"idoc-request": RELATIONSHIP_IDOC, "idoc-response": RELATIONSHIP_IDOC}});
    };

    const already = useMemo(() => new Set((edges ?? []).map(e => String(e.ChildUSID ?? ""))), [edges]);

    return (
        // THE BACKING THE OTHER SECTIONS HAVE: a bordered, rounded panel.
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "12px 14px"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    {title}{edges ? ` (${edges.length})` : ""}
                </Typography>
                <Box sx={{flex: 1}}/>
                <Button size="small" variant="outlined" sx={{textTransform: "none"}}
                        disabled={!typeUSID || !draft} onClick={() => setAdding(true)}
                        title={draft ? undefined : "Purchase orders are added to a draft; revise the contract to change them."}>
                    Add purchase order
                </Button>
            </Box>
            {problem && (
                <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "8px"}}>{problem}</Typography>
            )}
            {typeUSID === null && edges === null && (
                <Typography sx={{fontSize: 13, color: Grey7}}>Reading…</Typography>
            )}
            {edges && edges.length === 0 && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                    No purchase orders are placed under this agreement yet.
                </Typography>
            )}
            {edges && edges.length > 0 && (
                <Box sx={{display: "grid", gridTemplateColumns: "1.2fr 3fr 1fr auto", gap: "8px 12px",
                          alignItems: "center"}}>
                    {["ID", "Name", "Status", ""].map((head, index) => (
                        <Typography key={`${head}${index}`}
                                    sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase",
                                         letterSpacing: "0.04em"}}>{head}</Typography>
                    ))}
                    {edges.map(edge => (
                        <React.Fragment key={edge.USID}>
                            <Typography sx={{fontSize: 13, color: Blue3}}>{edge.ChildObjectID ?? "—"}</Typography>
                            <Typography sx={{fontSize: 13, color: Grey17}}>{edge.ChildName ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{edge.ChildStatus ?? "—"}</Typography>
                            <Typography sx={{fontSize: 12.5, color: Grey7, cursor: "pointer"}}
                                        onClick={() => removing !== edge.USID && remove(edge)}>
                                {removing === edge.USID ? "Removing…" : "Remove"}
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
            )}
            {adding && (
                <PurchaseOrderChooser api={api} already={already}
                                      onClose={() => setAdding(false)}
                                      onChoose={add}
                                      onDone={() => { setAdding(false); load(); onChanged?.(); }}/>
            )}
        </Box>
    );
}

/**
 * The chooser: the Purchase Orders list screen, contained in a dialog.
 *
 * ED, 2026-10-28: "There may be thousands of purchase orders and I need to get the list narrowed.
 * So a popup that offered like a contained purchase order screen with the filter by options on the
 * left and the persona selectors (where permissions allow) and pagination." So it is that screen --
 * ObjectList in picker mode: Filter By on the left (status, type, the form's own facets, recency),
 * the visibility tabs the persona is allowed, the records and attachment search boxes, the grid
 * with its paging -- and a row click ticks the order. Select hands the ticked orders back; several
 * at once, since an agreement usually has more than one.
 */
function PurchaseOrderChooser({already, onClose, onChoose, onDone}: {
    api: any; already: Set<string>; onClose: () => void; onChoose: (order: Order) => Promise<any>;
    /** Every ticked order is under the agreement: the host closes the dialog and reads again. */
    onDone: () => void;
}) {
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [ticked, setTicked] = useState<PickedRecord[]>([]);
    const classification = OBJECT_CLASSIFICATIONS.find(one => one.code === "PurchaseOrder");
    if (!classification) return null;

    const select = () => {
        const fresh = ticked.filter(one => one.ObjectUSID && !already.has(String(one.ObjectUSID)));
        if (fresh.length === 0) {
            setProblem(ticked.length ? "Every order ticked is already under this agreement."
                                     : "Nothing is ticked.");
            return;
        }
        setBusy(true);
        Promise.all(fresh.map(one => onChoose({ObjectUSID: String(one.ObjectUSID), ObjectID: one.ObjectID,
                                               Name: one.Name, Status: one.Status})))
            .then(() => onDone())
            .catch((failure: any) => {
                setProblem(failure?.response?.data?.Cause ?? "A purchase order could not be added.");
                setBusy(false);
            });
    };

    // SIZING. The paper is a fixed-height flex column; the content is the one flexible row and
    // CLIPS, so the list page inside it (itself a flex column that scrolls its own middle) can
    // never push the footer off the bottom. The footer is the dialog's own, bottom right.
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xl"
                slotProps={{paper: {sx: {height: "90vh", maxHeight: "90vh", display: "flex", flexDirection: "column"}}}}>
            <DialogTitle sx={{fontSize: 16, paddingBottom: "6px", flex: "0 0 auto"}}>
                Add purchase orders
                {busy ? <span style={{fontSize: 13, color: Grey7, marginLeft: 12}}>Adding…</span> : null}
            </DialogTitle>
            {problem && (
                <Typography sx={{fontSize: 13, color: "#B3261E", padding: "0 24px 6px", flex: "0 0 auto"}}>{problem}</Typography>
            )}
            <DialogContent sx={{padding: 0, flex: "1 1 auto", minHeight: 0, overflow: "hidden",
                                display: "flex", flexDirection: "column"}}>
                <ObjectList classification={classification}
                            picker={{multiple: true, onSelectionChange: setTicked}}/>
            </DialogContent>
            <DialogActions sx={{padding: "12px 24px", borderTop: "1px solid #E4E7EC", flex: "0 0 auto"}}>
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={select}
                        disabled={busy || ticked.length === 0} sx={{textTransform: "none"}}>
                    Select{ticked.length > 0 ? ` (${ticked.length})` : ""}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
